/**
 * useRouteBoard — map-ready data for the Route Preview board.
 *
 * Wraps the same `useRoutes` query that powers `useRoutePreview` (React Query
 * dedupes by key, so this is free), but returns coordinate-bearing `BoardRoute`s
 * for the map + drag-to-reorder panel, plus the raw records keyed by id for
 * persistence.
 */
import { useMemo } from 'react';
import { useRoutes } from './useTechnicianRoutes';
import { formatRouteDate } from '@/lib/supabase/technicianRoutes';
import { getTechnicianColor } from '@/features/recalls/utils/technicianColors';
import { storedRouteToBoardRoute, type BoardRoute } from '../utils/routeBoardMapping';
import type { TechnicianRouteWithDetails } from '@/types/technicianRoutes';

interface UseRouteBoardOptions {
  routeDate: Date | string;
  /** Optional roster for stable technician colors (matches the recall map). */
  roster?: Array<{ id: string }>;
  enabled?: boolean;
}

interface UseRouteBoardReturn {
  boardRoutes: BoardRoute[];
  recordsById: Record<string, TechnicianRouteWithDetails>;
  loading: boolean;
  error: string | null;
}

export function useRouteBoard({
  routeDate,
  roster,
  enabled = true
}: UseRouteBoardOptions): UseRouteBoardReturn {
  const formattedDate = formatRouteDate(routeDate);

  const {
    data: storedRoutes,
    isLoading,
    error
  } = useRoutes({
    startDate: formattedDate,
    endDate: formattedDate,
    enabled
  });

  // Active (non-completed) routes only — mirrors the preview page.
  const activeRecords = useMemo(
    () => (storedRoutes || []).filter(route => route.status !== 'completed'),
    [storedRoutes]
  );

  // Build a stable roster for colors: prefer the passed roster, else derive
  // from the technicians present in today's routes (id-sorted for stability).
  const effectiveRoster = useMemo(() => {
    if (roster && roster.length > 0) return roster;
    const ids = Array.from(new Set(activeRecords.map(r => r.technician_id)));
    return ids.map(id => ({ id }));
  }, [roster, activeRecords]);

  const boardRoutes = useMemo(
    () =>
      activeRecords.map(record =>
        storedRouteToBoardRoute(record, getTechnicianColor(record.technician_id, effectiveRoster))
      ),
    [activeRecords, effectiveRoster]
  );

  const recordsById = useMemo(() => {
    const map: Record<string, TechnicianRouteWithDetails> = {};
    activeRecords.forEach(record => {
      map[record.id] = record;
    });
    return map;
  }, [activeRecords]);

  return {
    boardRoutes,
    recordsById,
    loading: isLoading,
    error: error?.message || null
  };
}

export default useRouteBoard;
